import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getBiddingInfo, getLoadTimeForItemBidding } from '@/redux/modules/biddingInfo';
import { getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/user';
import { openAbsenteeBidModal, openSendSellerMessageModal, openWaitingForApprovalModal } from '@/redux/modules/modal';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import BidCountDown from '@/components/BidCountDown/BidCountDown';
import BiddingConsoleButton from '@/pages/BiddingConsole/BiddingConsoleButton';
import BidHistoryTable from '@/components/BidHistoryTable/BidHistoryTable';
import BidMessage from '@/components/BidMessage/BidMessage';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { getBidHistory } from '@/redux/modules/bidHistory';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getItem } from '@/redux/modules/item';
import { getItemDetail } from '@/redux/modules/itemDetail';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import { getSeller } from '@/redux/modules/seller';
import { isAfterStartTimeOnStartDay } from '@/utils/isAfterStartTimeOnStartDay';
import isEqual from 'react-fast-compare';
import ItemBiddingAbsentee from './ItemBiddingAbsentee';
import ItemBiddingEnterButton from './ItemBiddingEnterButton';
import ItemBiddingEstimate from './ItemBiddingEstimate';
import ItemBiddingSaveButton from './ItemBiddingSaveButton';
import ItemBiddingSellSimilarButton from './ItemBiddingSellSimilarButton';
import ItemBiddingStatusRow from './ItemBiddingStatusRow';
import ItemBidToast from './ItemBidToast';
import RegistrationMessage from '@/components/BidMessage/RegistrationMessage';
import RequestOnlineBiddingButton from './RequestOnlineBiddingButton';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    itemId: number;
    onCountdownEnd: Function;
};

const ItemBidding = ({ itemId, onCountdownEnd }: Props) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();

    const { catalogId, lotNumber = '', sellerId = 0, startPrice = 0, title } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );
    const { currency = '', isCatalogOnly = false, isTimed = false, saleStartTs = 0, title: catalogTitle } = useSelector(
        (state) => getCatalog(state, catalogId),
        isEqual
    );
    const { approved, blockedByAuctioneer, declined, pending, suspended } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );
    const { name: sellerName } = useSelector((state) => getSeller(state, sellerId));
    const dynamicDataLoaded = Boolean(useSelector((state) => getLoadTimeForItemBidding(state, itemId)));
    const { isPassed = false, isSold = false } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { externalUrl } = useSelector((state) => getItemDetail(state, itemId), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);
    const accountInGoodStanding = useSelector((state) => getUserIsInGoodStanding(state), isEqual);
    const timeStamp = useSelector((state) => getCountDown(state, itemId), isEqual);
    const userLoggedIn = useSelector((state) => isUserLoggedIn(state), isEqual);
    const bidHistory = useSelector((state) => getBidHistory(state, itemId), isEqual);

    const mobile = useIsNarrowerThan(theme.breakpoints.tabletNarrowWidthNum);

    const [showBidHistory, setShowBidHistory] = React.useState(false);

    const toggleBidHistory = React.useCallback(() => setShowBidHistory(!showBidHistory), [showBidHistory]);

    const biddingBoxStyleChecks = {
        appproved: isLotOpen && approved,
        loading: !dynamicDataLoaded,
        notApproved: isLotOpen && (!accountInGoodStanding || declined || blockedByAuctioneer || suspended),
        pending: isLotOpen && pending,
    };

    const showCountDown = isTimed && Boolean(timeStamp) && approved;

    const submitRegister = React.useCallback(
        (attrs) => {
            dispatch(submitRegisterForCatalog(attrs));
        },
        [dispatch]
    );

    const showWaitingForApprovalModal = React.useCallback(() => {
        dispatch(openWaitingForApprovalModal(catalogId));
    }, [dispatch, catalogId]);

    const showAbsenteeBidModal = React.useCallback(
        (itemId: number, bidAmount: number) => {
            dispatch(openAbsenteeBidModal(itemId, bidAmount));
        },
        [dispatch]
    );

    const showSendSellerMessageModal = React.useCallback(
        (formatObject) => {
            const messageDetails = {
                catalogId,
                itemId,
                lotNumber,
                title,
            };
            //'registration.issue'
            const subject = formatMessage({ id: formatObject.id }, formatObject.values);
            return () => {
                dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
            };
        },
        [catalogId, dispatch, formatMessage, itemId, lotNumber, sellerId, sellerName, title]
    );

    const showSendSellerMessageModalDeclined = React.useCallback(() => {
        const messageDetails = {
            catalogId,
            message: `${formatMessage({ id: 'message_registration_declined' })}`,
            title: catalogTitle,
        };

        const subject = `${formatMessage({ id: 'registration_declined' })}: ${catalogTitle}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, catalogTitle, dispatch, formatMessage, sellerId, sellerName]);

    return (
        <>
            <ItemBidToast catalogId={catalogId} itemId={itemId} />
            {mobile && <LotNumberSpan>lot {lotNumber}</LotNumberSpan>}
            <ItemBiddingEstimate catalogId={catalogId} itemId={itemId} onCountdownEnd={onCountdownEnd} />
            <BiddingBoxDiv biddingBoxStyleChecks={biddingBoxStyleChecks}>
                <BodyDiv>
                    <BidMessage
                        itemId={itemId}
                        showSendSellerMessageModal={showSendSellerMessageModal}
                        showWaitingForApprovalModal={showWaitingForApprovalModal}
                    />
                    {((isAfterStartTimeOnStartDay(saleStartTs) && isCatalogOnly) ||
                        (isCatalogOnly && !isSold && !isPassed)) && (
                        <RequestOnlineBiddingButton
                            catalogId={catalogId}
                            itemId={itemId}
                            submitRegisterForCatalog={submitRegister}
                            text={
                                externalUrl ? (
                                    <FormattedMessage id="register_partners_site" />
                                ) : (
                                    <FormattedMessage id="request_online_bidding" />
                                )
                            }
                        />
                    )}
                    <ItemBiddingStatusRow catalogId={catalogId} itemId={itemId} toggleBidHistory={toggleBidHistory} />
                    <DisplayOnPrintSpan>
                        <FormattedMessage id="starting_bid" />{' '}
                        <StyledFormattedCurrencyStart convert noFraction sourceCurrency={currency} value={startPrice} />
                    </DisplayOnPrintSpan>
                    {isLotOpen ? (
                        <>
                            <BiddingConsoleButton catalogId={catalogId} itemId={itemId} noLinks />
                            {showCountDown && <BidCountDown timeStamp={timeStamp} />}
                        </>
                    ) : (
                        <ItemBiddingAbsentee
                            catalogId={catalogId}
                            itemId={itemId}
                            openAbsenteeBidModal={showAbsenteeBidModal}
                            openSendSellerMessageModal={showSendSellerMessageModal}
                        />
                    )}
                    {userLoggedIn && (isSold || isPassed) && (
                        <>
                            <BidHistoryTable catalogId={catalogId} itemId={itemId} showBidHistory={showBidHistory} />
                            {bidHistory.length > 3 && ( // see above comment
                                <ShowBidHistoryDiv onClick={toggleBidHistory}>
                                    <Text colorPrimaryWeak secondary underline>
                                        <FormattedMessage
                                            id={showBidHistory ? 'hide_bid_history' : 'show_bid_history'}
                                        />
                                    </Text>
                                </ShowBidHistoryDiv>
                            )}
                        </>
                    )}
                    <ItemBiddingEnterButton catalogId={catalogId} />
                    <ItemBiddingSellSimilarButton catalogId={catalogId} itemId={itemId} sellerId={sellerId} />
                    <ItemBiddingSaveButton itemId={itemId} />
                </BodyDiv>
            </BiddingBoxDiv>
            <StyledRegistrationMessage
                catalogId={catalogId}
                itemId={itemId}
                showSendSellerMessageModalDeclined={showSendSellerMessageModalDeclined}
            />
        </>
    );
};

export default React.memo<Props>(ItemBidding, isEqual);

const LotNumberSpan = styled.span`
    display: inline-block;
    font-size: 12px;
    text-transform: uppercase;
`;

const BodyDiv = styled.div`
    flex: 1 1 auto;
    padding: 30px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 15px;
    }

    @media print {
        padding: 0;
    }

    & > * {
        margin-bottom: 30px;
    }

    & > a {
        display: block;
    }

    & > *:last-child {
        margin-bottom: 0;
    }

    > div:not(:first-child),
    a {
        @media print {
            display: none;
        }
    }

    > div:first-child {
        @media print {
            margin: 0;
        }
    }
`;

const DisplayOnPrintSpan = styled.span`
    @media screen {
        display: none;
    }

    display: flex;
    justify-content: center;
    width: 100%;
    margin: 7px auto;
`;

const StyledFormattedCurrencyStart = styled(FormattedCurrency)`
    padding-left: 7px;
`;

const ShowBidHistoryDiv = styled.div`
    text-align: center;
    margin: -15px 0 20px;
    cursor: pointer;
`;

const StyledRegistrationMessage = styled(RegistrationMessage)`
    margin: 30px 0;

    @media print {
        margin: 0;
    }
`;

const Approved = css`
    background-color: ${({ theme }) => theme.colors.green200};
`;
const Loading = css`
    opacity: 0;
`;
const NotApproved = css`
    background-color: ${({ theme }) => theme.colors.red400};
`;
const Pending = css`
    background-color: ${({ theme }) => theme.colors.grey500};
`;

const BiddingBoxDiv = styled.div<{ biddingBoxStyleChecks }>`
    ${(props) => props.biddingBoxStyleChecks.approved && Approved}
    ${(props) => props.biddingBoxStyleChecks.loading && Loading}
    ${(props) => props.biddingBoxStyleChecks.notApproved && NotApproved}
    ${(props) => props.biddingBoxStyleChecks.pending && Pending}
    background: ${({ theme }) => theme.colors.beige100};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    display: flex;
    flex-direction: column;
    margin-top: 9px;
    opacity: 1;
    transition: opacity 0.1s ease-in;

    @media print {
        border: none;
        margin: 0;
    }
`;
